/**
 * sharingUtils.ts
 * Utility functions for flowchart sharing and permissions
 * Fully type-safe TypeScript implementation
 */

import { logger } from '../lib/logger';
import supabaseClient from '../supabase';
import type {
  UserRole,
  SupabaseUser,
  UserAccessInfo,
  PendingInvitation,
  ShareTokenVerification,
  InvitationResult,
  RoleBadge,
} from '../types';

// Use supabase for database operations
const supabase = supabaseClient;

// =====================================================
// INTERNAL HELPERS
// =====================================================

/**
 * Get current user from Supabase session (always up to date, no localStorage)
 * @internal
 */
async function getCurrentUser(): Promise<SupabaseUser | null> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) return null;
    return session.user as SupabaseUser;
  } catch (err) {
    logger.error('Error in getCurrentUser:', err);
    return null;
  }
}

/**
 * Role hierarchy for permission checks
 * Higher number = more permissions
 */
const roleHierarchy: Record<UserRole, number> = {
  viewer: 1,
  editor: 2,
  owner: 3,
} as const;

/**
 * Check if a role has sufficient permissions
 * @internal
 */
function hasRolePermission(userRole: UserRole, requiredRole: UserRole): boolean {
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

// =====================================================
// PERMISSION CHECKS
// =====================================================

/**
 * Check if current user has access to a flowchart
 * Checks both flowchart_access and project_access (via getUserRole fallback)
 */
export async function hasAccess(
  flowchartId: string,
  requiredRole: UserRole = 'viewer'
): Promise<boolean> {
  try {
    const role = await getUserRole(flowchartId);
    if (!role) return false;
    return hasRolePermission(role, requiredRole);
  } catch (err) {
    logger.error('Error checking access:', err);
    return false;
  }
}

/**
 * Get current user's role for a flowchart
 */
export async function getUserRole(flowchartId: string): Promise<UserRole | null> {
  try {
    const user = await getCurrentUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('flowchart_access')
      .select('role')
      .eq('flowchart_id', flowchartId)
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error getting user role:', error);
      return null;
    }

    if (data && data.length > 0) {
      return data[0].role as UserRole;
    }

    // Geen flowchart_access rij — controleer of user toegang heeft via project-level sharing
    const { data: fcData } = await supabase
      .from('flowcharts')
      .select('project_id')
      .eq('id', flowchartId)
      .single();

    if (fcData?.project_id) {
      const { data: paData } = await supabase
        .from('project_access')
        .select('role')
        .eq('project_id', fcData.project_id)
        .eq('user_id', user.id)
        .single();

      if (paData?.role) return paData.role as UserRole;
    }

    return null;
  } catch (err) {
    logger.error('Error getting user role:', err);
    return null;
  }
}

/**
 * Check if user is owner of a flowchart
 */
export async function isOwner(flowchartId: string): Promise<boolean> {
  const role = await getUserRole(flowchartId);
  return role === 'owner';
}

/**
 * Check if user can edit a flowchart (owner or editor)
 */
export async function canEdit(flowchartId: string): Promise<boolean> {
  return await hasAccess(flowchartId, 'editor');
}

// =====================================================
// ACCESS MANAGEMENT
// =====================================================

/**
 * Get all users who have access to a flowchart.
 * Includes both direct (flowchart_access) and inherited (project_access) users.
 * Inherited users have via_project: true and cannot be managed from the flowchart share dialog.
 */
export async function getFlowchartUsers(flowchartId: string): Promise<UserAccessInfo[]> {
  try {
    // Direct flowchart access
    const { data: accessData, error: accessError } = await supabase
      .from('flowchart_access')
      .select('id, user_id, role, granted_at, granted_by')
      .eq('flowchart_id', flowchartId);

    if (accessError) {
      logger.error('Error getting flowchart users:', accessError);
      throw accessError;
    }

    const directUserIds = new Set<string>((accessData || []).map((a: any) => a.user_id));

    // Check if flowchart belongs to a project and fetch project-level access
    const { data: fcData } = await supabase
      .from('flowcharts')
      .select('project_id')
      .eq('id', flowchartId)
      .single();

    let projectAccessData: any[] = [];
    if (fcData?.project_id) {
      const { data: paData } = await supabase
        .from('project_access')
        .select('id, user_id, role, granted_at, granted_by')
        .eq('project_id', fcData.project_id);
      // Only include users not already in direct access
      projectAccessData = (paData || []).filter((a: any) => !directUserIds.has(a.user_id));
    }

    // Batch-fetch emails for all unique user IDs
    const allUserIds = [
      ...(accessData || []).map((a: any) => a.user_id),
      ...projectAccessData.map((a: any) => a.user_id),
    ];
    const uniqueUserIds = [...new Set(allUserIds)];
    const { data: profiles } = uniqueUserIds.length > 0
      ? await supabase.from('user_profiles').select('id, email').in('id', uniqueUserIds)
      : { data: [] };

    const emailMap: Record<string, string> = {};
    (profiles || []).forEach((p: any) => { emailMap[p.id] = p.email; });

    const directUsers: UserAccessInfo[] = (accessData || []).map((access: any) => ({
      id: access.id,
      flowchart_id: flowchartId,
      user_id: access.user_id,
      email: emailMap[access.user_id],
      role: access.role as UserRole,
      granted_at: access.granted_at,
      granted_by: access.granted_by,
      via_project: false,
    }));

    const projectUsers: UserAccessInfo[] = projectAccessData.map((access: any) => ({
      id: access.id,
      flowchart_id: flowchartId,
      user_id: access.user_id,
      email: emailMap[access.user_id],
      role: access.role as UserRole,
      granted_at: access.granted_at,
      granted_by: access.granted_by,
      via_project: true,
    }));

    return [...directUsers, ...projectUsers];
  } catch (err) {
    logger.error('Error getting flowchart users:', err);
    return [];
  }
}

/**
 * Grant access to a user
 */
export async function grantAccess(
  flowchartId: string,
  userId: string,
  role: UserRole,
  skipOwnerCheck: boolean = false
): Promise<boolean> {
  try {
    const currentUser = await getCurrentUser();
    if (!currentUser) return false;

    // Check if current user is owner (unless skipped for share links)
    if (!skipOwnerCheck) {
      const isOwnerCheck = await isOwner(flowchartId);
      if (!isOwnerCheck) {
        logger.error('Only owners can grant access');
        return false;
      }
    }

    const { error } = await supabase
      .from('flowchart_access')
      .insert({
        flowchart_id: flowchartId,
        user_id: userId,
        role: role,
        granted_by: currentUser.id
      });

    if (error) throw error;
    return true;
  } catch (err) {
    logger.error('Error granting access:', err);
    return false;
  }
}

/**
 * Update user's role for a flowchart
 */
export async function updateUserRole(
  flowchartId: string,
  userId: string,
  newRole: UserRole
): Promise<boolean> {
  try {
    // Check if current user is owner
    const isOwnerCheck = await isOwner(flowchartId);
    if (!isOwnerCheck) {
      logger.error('Only owners can update roles');
      return false;
    }

    const { error } = await supabase
      .from('flowchart_access')
      .update({ role: newRole })
      .eq('flowchart_id', flowchartId)
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  } catch (err) {
    logger.error('Error updating user role:', err);
    return false;
  }
}

/**
 * Remove user's access to a flowchart
 */
export async function removeAccess(
  flowchartId: string,
  userId: string
): Promise<boolean> {
  try {
    // Check if current user is owner
    const isOwnerCheck = await isOwner(flowchartId);
    if (!isOwnerCheck) {
      logger.error('Only owners can remove access');
      return false;
    }

    // Don't allow removing the owner's own access
    const currentUser = await getCurrentUser();
    if (currentUser && userId === currentUser.id) {
      logger.error('Cannot remove own access');
      return false;
    }

    const { error } = await supabase
      .from('flowchart_access')
      .delete()
      .eq('flowchart_id', flowchartId)
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  } catch (err) {
    logger.error('Error removing access:', err);
    return false;
  }
}

// =====================================================
// SHARE LINK MANAGEMENT
// =====================================================

/**
 * Generate random share token
 * @returns 32-character hexadecimal string
 */
export function generateShareToken(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Enable or disable link sharing for a flowchart
 */
export async function setLinkSharing(
  flowchartId: string,
  enabled: boolean
): Promise<boolean> {
  try {
    // Check if user is owner
    const isOwnerCheck = await isOwner(flowchartId);
    if (!isOwnerCheck) {
      logger.error('Only owners can manage link sharing');
      return false;
    }

    // Update or create share settings
    const { error } = await supabase
      .from('flowchart_share_settings')
      .upsert({
        flowchart_id: flowchartId,
        link_sharing_enabled: enabled,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'flowchart_id'
      });

    if (error) throw error;
    return true;
  } catch (err) {
    logger.error('Error setting link sharing:', err);
    return false;
  }
}

/**
 * Get share link for a flowchart
 * Returns null if link sharing is not enabled
 */
export async function getShareLink(flowchartId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('flowchart_share_settings')
      .select('share_token, link_sharing_enabled')
      .eq('flowchart_id', flowchartId)
      .single();

    if (error || !data) return null;

    if (!data.link_sharing_enabled) return null;

    // Construct share link URL
    const baseUrl = window.location.origin;
    return `${baseUrl}/flowchart/${flowchartId}?token=${data.share_token}`;
  } catch (err) {
    logger.error('Error getting share link:', err);
    return null;
  }
}

/**
 * Regenerate share token (invalidates old share links)
 */
export async function regenerateShareToken(flowchartId: string): Promise<string | null> {
  try {
    // Check if user is owner
    const isOwnerCheck = await isOwner(flowchartId);
    if (!isOwnerCheck) {
      logger.error('Only owners can regenerate share tokens');
      return null;
    }

    const newToken = generateShareToken();

    const { error } = await supabase
      .from('flowchart_share_settings')
      .update({
        share_token: newToken,
        updated_at: new Date().toISOString()
      })
      .eq('flowchart_id', flowchartId);

    if (error) throw error;

    // Return new share link
    const baseUrl = window.location.origin;
    return `${baseUrl}/flowchart/${flowchartId}?token=${newToken}`;
  } catch (err) {
    logger.error('Error regenerating share token:', err);
    return null;
  }
}

/**
 * Verify share token from URL and grant access if valid
 */
export async function verifyShareToken(
  flowchartId: string,
  shareToken: string
): Promise<ShareTokenVerification> {
  try {
    // Check if token is valid (no user required for this check)
    const { data: settings, error } = await supabase
      .from('flowchart_share_settings')
      .select('share_token, link_sharing_enabled, link_access_role')
      .eq('flowchart_id', flowchartId);

    if (error || !settings || settings.length === 0) {
      return { valid: false };
    }

    const { share_token, link_sharing_enabled, link_access_role } = settings[0];
    
    if (!link_sharing_enabled || share_token !== shareToken) {
      return { valid: false };
    }

    // Token is valid! For logged-in users, grant access to database
    const user = await getCurrentUser();
    if (user) {
      // Check if user already has access
      const existingRole = await getUserRole(flowchartId);
      if (!existingRole) {
        // Grant access with the specified role (skip owner check for share links)
        await grantAccess(flowchartId, user.id, link_access_role as UserRole, true);
      }
    }

    // Return valid with the role from share settings
    return { valid: true, role: link_access_role as UserRole };
  } catch (err) {
    logger.error('Error verifying share token:', err);
    return { valid: false };
  }
}

// =====================================================
// INVITATIONS
// =====================================================

/**
 * Send invitation to email
 * NOTE: Currently only creates database entry, does not send actual email
 */
export async function sendInvitation(
  flowchartId: string,
  email: string,
  role: UserRole,
  message: string = ''
): Promise<InvitationResult> {
  try {
    const currentUser = await getCurrentUser();
    if (!currentUser) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if current user is owner
    const isOwnerCheck = await isOwner(flowchartId);
    if (!isOwnerCheck) {
      return { success: false, error: 'Only owners can send invitations' };
    }

    // Generate unique invite token
    const inviteToken = generateShareToken();

    // Cancel any existing pending invitations for the same flowchart+email
    await supabase
      .from('flowchart_invitations')
      .update({ status: 'cancelled' })
      .eq('flowchart_id', flowchartId)
      .eq('email', email)
      .eq('status', 'pending');

    const { data, error } = await supabase
      .from('flowchart_invitations')
      .insert({
        flowchart_id: flowchartId,
        email: email,
        role: role,
        invite_token: inviteToken,
        invited_by: currentUser.id,
        message: message,
        status: 'pending'
      })
      .select('id')
      .single();

    if (error) throw error;

    // Call Edge Function to send the invitation email
    const { error: fnError } = await supabase.functions.invoke('send-invitation', {
      body: { invitationId: data.id },
    });

    if (fnError) {
      // Log but don't fail — invitation is in DB, email is best-effort
      logger.error('Failed to send invitation email:', fnError);
    }

    return { success: true, inviteId: data.id };
  } catch (err) {
    logger.error('Error sending invitation:', err);
    const message = err instanceof Error
      ? err.message
      : (err as any)?.message || JSON.stringify(err);
    return { success: false, error: message };
  }
}

/**
 * Get pending invitations for a flowchart
 */
export async function getPendingInvitations(
  flowchartId: string
): Promise<PendingInvitation[]> {
  try {
    const { data, error } = await supabase
      .from('flowchart_invitations')
      .select('id, email, role, message, invited_by, invited_at')
      .eq('flowchart_id', flowchartId)
      .eq('status', 'pending');

    if (error) throw error;
    if (!data) return [];

    return data.map((invite: any) => ({
      id: invite.id,
      email: invite.email,
      role: invite.role as UserRole,
      message: invite.message,
      invited_by: invite.invited_by,
      created_at: invite.invited_at,
    }));
  } catch (err) {
    logger.error('Error getting pending invitations:', err);
    return [];
  }
}

/**
 * Cancel a pending invitation
 */
export async function cancelInvitation(invitationId: string): Promise<boolean> {
  try {
    const currentUser = await getCurrentUser();
    if (!currentUser) return false;

    // Update status to cancelled (only if user is the one who sent it)
    const { error } = await supabase
      .from('flowchart_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId)
      .eq('invited_by', currentUser.id);

    if (error) throw error;
    return true;
  } catch (err) {
    logger.error('Error cancelling invitation:', err);
    return false;
  }
}

// =====================================================
// UTILITIES
// =====================================================

/**
 * Get role badge configuration for UI display
 */
export function getRoleBadge(role: UserRole): RoleBadge {
  switch (role) {
    case 'owner':
      return {
        icon: 'crown',
        color: 'text-yellow-600',
        label: 'Owner'
      };
    case 'editor':
      return {
        icon: 'edit',
        color: 'text-blue-600',
        label: 'Editor'
      };
    case 'viewer':
      return {
        icon: 'eye',
        color: 'text-gray-600',
        label: 'Viewer'
      };
  }
}

/**
 * Export getCurrentUser for use in other modules
 */
export { getCurrentUser };